import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useCatalystFeed } from '@/hooks/useCatalystFeed';
import { useMapStore } from '@/stores/mapStore';
import CatalystCard from './CatalystCard';
import CatalystSkeleton from './CatalystSkeleton';
import CatalystDetailModal from './CatalystDetailModal';
import { cn } from '@/lib/utils';

export default function CatalystFeed() {
  const [selectedCatalyst, setSelectedCatalyst] = useState(null);
  const isSidebarOpen = useMapStore((s) => s.isSidebarOpen);
  const setSidebarOpen = useMapStore((s) => s.setSidebarOpen);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useCatalystFeed();

  const { ref: sentinelRef } = useInView({
    onChange: (inView) => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
  });

  const catalysts = data?.pages.flatMap((page) => page.items ?? []) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return (
    <>
      {/* Backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-xs lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar panel */}
      <aside
        className={cn(
          'fixed right-0 top-0 z-40 flex h-full w-full max-w-sm flex-col border-l border-neutral-200 bg-paper shadow-2xl transition-transform duration-300',
          isSidebarOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-ink">Catalysts</h2>
            {total > 0 && (
              <p className="text-xs text-neutral-500">{total.toLocaleString()} catalysts in this view</p>
            )}
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            aria-label="Close catalysts panel"
            className="rounded-lg p-1.5 text-neutral-500 transition hover:bg-parchment hover:text-ink"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Catalyst list */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {isLoading && (
            <>
              <CatalystSkeleton />
              <CatalystSkeleton />
              <CatalystSkeleton />
            </>
          )}

          {isError && (
            <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-[#003cc5] border border-red-200">
              Failed to load catalysts. Please try again.
            </div>
          )}

          {!isLoading && catalysts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <svg className="mb-3 h-12 w-12 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              <p className="text-sm text-neutral-500">No catalysts found in this region.</p>
              <p className="mt-1 text-xs text-neutral-400">Try zooming out or adjusting the date range.</p>
            </div>
          )}

          {catalysts.map((catalyst) => (
            <CatalystCard
              key={catalyst.id}
              catalyst={catalyst}
              onClick={() => setSelectedCatalyst(catalyst)}
            />
          ))}

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="py-1" />

          {isFetchingNextPage && (
            <div className="flex justify-center py-4">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#003cc5] border-t-transparent" />
            </div>
          )}

          {!hasNextPage && catalysts.length > 0 && (
            <p className="py-4 text-center text-xs text-neutral-400">All catalysts loaded</p>
          )}
        </div>
      </aside>
      {selectedCatalyst && (
        <CatalystDetailModal
          catalyst={selectedCatalyst}
          onClose={() => setSelectedCatalyst(null)}
        />
      )}
    </>
  );
}
